import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

const STORAGE_KEY_V81 = 'alfredapi.scroll-positions.v8.1';

const readPositionsV81 = () => {
  try {
    const stored = JSON.parse(window.sessionStorage.getItem(STORAGE_KEY_V81));
    return stored && typeof stored === 'object' ? stored : {};
  } catch (_) {
    return {};
  }
};

const scrollPositionsV81 = readPositionsV81();

const persistPositionsV81 = () => {
  try {
    window.sessionStorage.setItem(
      STORAGE_KEY_V81,
      JSON.stringify(scrollPositionsV81)
    );
  } catch (_) {}
};

const ScrollRestorationV81 = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const activeKeyRef = useRef(location.key);
  const restoringRef = useRef(false);
  const frameRef = useRef(0);

  useEffect(() => {
    const previousBehavior = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';

    const capturePosition = () => {
      if (restoringRef.current || frameRef.current) return;
      frameRef.current = window.requestAnimationFrame(() => {
        frameRef.current = 0;
        scrollPositionsV81[activeKeyRef.current] = Math.max(
          0,
          Math.round(window.scrollY)
        );
      });
    };
    const capturePositionNow = () => {
      if (restoringRef.current) return;
      scrollPositionsV81[activeKeyRef.current] = Math.max(
        0,
        Math.round(window.scrollY)
      );
    };
    const persistOnHide = () => {
      if (document.visibilityState === 'hidden') persistPositionsV81();
    };

    window.addEventListener('scroll', capturePosition, { passive: true });
    window.addEventListener('pagehide', persistPositionsV81);
    document.addEventListener('click', capturePositionNow, true);
    document.addEventListener('visibilitychange', persistOnHide);
    return () => {
      window.removeEventListener('scroll', capturePosition);
      window.removeEventListener('pagehide', persistPositionsV81);
      document.removeEventListener('click', capturePositionNow, true);
      document.removeEventListener('visibilitychange', persistOnHide);
      if (frameRef.current) window.cancelAnimationFrame(frameRef.current);
      persistPositionsV81();
      window.history.scrollRestoration = previousBehavior;
    };
  }, []);

  useEffect(() => {
    const previousKey = activeKeyRef.current;
    if (previousKey !== location.key) {
      persistPositionsV81();
    }
    activeKeyRef.current = location.key;

    let timer = 0;
    let cancelled = false;
    const targetPosition =
      navigationType === 'POP'
        ? Number(scrollPositionsV81[location.key] || 0)
        : 0;

    if (navigationType !== 'POP' || targetPosition <= 0) {
      restoringRef.current = true;
      window.scrollTo(0, 0);
      scrollPositionsV81[location.key] = 0;
      restoringRef.current = false;
      return undefined;
    }

    restoringRef.current = true;
    const deadline = window.performance.now() + 4000;
    const restoreWhenReady = () => {
      if (cancelled) return;
      const maxPosition = Math.max(
        0,
        document.documentElement.scrollHeight - window.innerHeight
      );
      const ready = maxPosition >= targetPosition;
      const expired = window.performance.now() >= deadline;
      window.scrollTo(0, Math.min(targetPosition, maxPosition));
      if (ready || expired) {
        restoringRef.current = false;
        scrollPositionsV81[location.key] = Math.min(
          targetPosition,
          maxPosition
        );
        return;
      }
      timer = window.setTimeout(restoreWhenReady, 50);
    };
    timer = window.setTimeout(restoreWhenReady, 0);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
      restoringRef.current = false;
    };
  }, [location.key, navigationType]);

  return null;
};

export default ScrollRestorationV81;
